/**
 * Tenant pilot mode settings — SSOT for `tenants.settings.pilot` slice.
 *
 * @see src/shared/platform/pilot-surface-registry.ts — surface allow/deny maps
 * @see .cursor/plans/archived/2026-q2/productization-7w/stream-p1-pilot-mode-surface-folding.md (P1a/P1b)
 */

/** Pilot preset identifiers — maps to `PILOT_PRESET_SURFACE_MAPS` in pilot-surface-registry. */
export const TENANT_PILOT_PRESET_IDS = ['smb_order_ops_v1'] as const;
export type TenantPilotPresetId = (typeof TENANT_PILOT_PRESET_IDS)[number];

/**
 * Tenant AI governance mode.
 * `assistive_only` — summarize/extract/suggest only; mutations blocked at gateway (AI0/AI1).
 */
export const TENANT_PILOT_AI_MODES = ['assistive_only'] as const;
export type TenantPilotAiMode = (typeof TENANT_PILOT_AI_MODES)[number];

export const DEFAULT_TENANT_PILOT_AI_MODE: TenantPilotAiMode = 'assistive_only';

export const DEFAULT_TENANT_PILOT_PRESET_ID: TenantPilotPresetId = 'smb_order_ops_v1';

/** Persisted tenant pilot slice — stored under `tenants.settings.pilot`. */
export interface TenantPilotSettings {
  /** When true, menu/route guards fold to preset enabled surfaces (P1b). */
  pilotMode: boolean;
  /** Surface map preset — e.g. `smb_order_ops_v1` for CP-PILOT-1 order-ops focus. */
  pilotPresetId?: TenantPilotPresetId;
  /** Optional override; values must be `PilotSurfaceId` — preset map is SSOT when omitted. */
  enabledSurfaces?: readonly string[];
  /** Optional override; values must be `PilotSurfaceId` — preset map is SSOT when omitted. */
  hiddenSurfaces?: readonly string[];
  /** AI behavior gate — SMB pilot defaults to assistive-only. */
  aiMode?: TenantPilotAiMode;
}

export const DEFAULT_SMB_ORDER_OPS_PILOT_SETTINGS: Readonly<
  Required<Pick<TenantPilotSettings, 'pilotMode' | 'pilotPresetId' | 'aiMode'>>
> = {
  pilotMode: true,
  pilotPresetId: DEFAULT_TENANT_PILOT_PRESET_ID,
  aiMode: DEFAULT_TENANT_PILOT_AI_MODE,
};

export function isTenantPilotPresetId(value: string): value is TenantPilotPresetId {
  return (TENANT_PILOT_PRESET_IDS as readonly string[]).includes(value);
}

export function isTenantPilotAiMode(value: string): value is TenantPilotAiMode {
  return (TENANT_PILOT_AI_MODES as readonly string[]).includes(value);
}

export function resolveTenantPilotAiMode(
  settings?: Pick<TenantPilotSettings, 'aiMode'>
): TenantPilotAiMode {
  return settings?.aiMode ?? DEFAULT_TENANT_PILOT_AI_MODE;
}
